
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import moment from 'moment-timezone';

export default defineComponent({
  name: 'FlowScheduleBulkModal',

  components: {
    Dialog,
    ConfirmDialog,
    Calendar,
    Dropdown,
    SelectButton,
    SpmButton,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'schedule'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const isVisible = computed({
      get: () => props.visible,
      set: (value) => {
        if (!value) {
          emit('close');
        }
      },
    });

    // Send type: 'later' or 'now'
    const sendType = ref<'later' | 'now'>('later');

    // Date selection
    const minDate = new Date();
    const selectedDate = ref(new Date());

    // Time options (15 min intervals)
    const timeOptions = ref<{ value: string; label: string }[]>([]);
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const hours = String(i).padStart(2, '0');
        const minutes = String(j).padStart(2, '0');
        const timeString = `${hours}:${minutes}`;
        timeOptions.value.push({ value: timeString, label: timeString });
      }
    }
    const selectedTime = ref('08:00');

    // Timezone options
    const timezoneOptions = ref<{ label: string; code: string; items: { value: string; label: string }[] }[]>([]);
    const selectedTimezone = ref(moment.tz.guess() || 'UTC');

    const populateTimezoneOptions = () => {
      const allowedContinents = [
        'US', 'Europe', 'Africa', 'UTC', 'Indian', 'Canada', 'Brazil', 'Australia', 'Atlantic', 'Asia',
      ];

      const zones = moment.tz.names();
      const grouped: Record<string, string[]> = {};

      zones.forEach((zone) => {
        const parts = zone.split('/');
        const continent = parts[0];

        if (!allowedContinents.includes(continent)) {
          return;
        }

        if (!grouped[continent]) {
          grouped[continent] = [];
        }

        grouped[continent].push(zone);
      });

      Object.keys(grouped).forEach((continent) => {
        const continentTimezones = grouped[continent];
        const continentTimezonesOptions = continentTimezones.map((timezone: string) => {
          const timezoneLabel = timezone.replace(/_/g, ' ');
          return { value: timezone, label: timezoneLabel };
        });

        timezoneOptions.value.push({
          label: continent,
          code: continent,
          items: continentTimezonesOptions,
        });
      });

      // Add UTC as suggestion at the top
      const clientTimezone = moment.tz.guess();
      const suggestions = [{ value: 'UTC', label: 'UTC' }];
      if (clientTimezone && clientTimezone !== 'UTC') {
        suggestions.push({ value: clientTimezone, label: clientTimezone.replace(/_/g, ' ') });
      }

      timezoneOptions.value.unshift({
        label: t('flows.scheduleBulk.timezone.suggestion'),
        code: 'Suggestion',
        items: suggestions,
      });
    };

    // Perfect timing
    const perfectTiming = ref(0);
    const perfectTimingOptions = computed(() => [
      { value: 1, label: t('flows.scheduleBulk.perfectTiming.activate') },
      { value: 0, label: t('flows.scheduleBulk.perfectTiming.deactivate') },
    ]);

    // Slowdown
    const slowdown = ref(0);
    const slowdownOptions = computed(() => [
      { value: 1, label: t('yes') },
      { value: 0, label: t('no') },
    ]);

    // Interval between sends
    const intervalBetweenSends = ref('15');
    const intervalOptions = computed(() => [
      { value: '15', label: t('flows.scheduleBulk.interval.options.15') },
      { value: '30', label: t('flows.scheduleBulk.interval.options.30') },
      { value: '60', label: t('flows.scheduleBulk.interval.options.60') },
      { value: '90', label: t('flows.scheduleBulk.interval.options.90') },
      { value: '120', label: t('flows.scheduleBulk.interval.options.120') },
      { value: '150', label: t('flows.scheduleBulk.interval.options.150') },
      { value: '180', label: t('flows.scheduleBulk.interval.options.180') },
      { value: '210', label: t('flows.scheduleBulk.interval.options.210') },
      { value: '240', label: t('flows.scheduleBulk.interval.options.240') },
      { value: '270', label: t('flows.scheduleBulk.interval.options.270') },
      { value: '300', label: t('flows.scheduleBulk.interval.options.300') },
    ]);

    const handleClose = () => {
      emit('close');
    };

    const executeSchedule = () => {
      let timeSending: string;

      if (sendType.value === 'later') {
        const dateStr = moment(selectedDate.value as Date).format('YYYY-MM-DD');
        const dateTimeStr = `${dateStr} ${selectedTime.value}:00`;
        const clientMoment = moment.tz(dateTimeStr, selectedTimezone.value);
        const utcMoment = clientMoment.clone().utc();
        timeSending = utcMoment.format('YYYYMMDDHHmmss');
      } else {
        timeSending = moment.utc().format('YYYYMMDDHHmmss');
      }

      const scheduleParams = {
        send_type: sendType.value,
        time_sending: timeSending,
        timezone: selectedTimezone.value,
        perfect_timing: perfectTiming.value,
        slowdown: slowdown.value,
        interval_between_sends: slowdown.value === 1 ? intervalBetweenSends.value : null,
      };

      emit('schedule', scheduleParams);
    };

    const handleSchedule = () => {
      confirm.require({
        group: 'scheduleBulkConfirm',
        header: t('flows.scheduleBulk.confirm.header'),
        message: t('flows.scheduleBulk.confirm.message'),
        icon: 'far fa-calendar-check',
        acceptLabel: t('flows.scheduleBulk.confirm.accept'),
        rejectLabel: t('flows.scheduleBulk.confirm.reject'),
        accept: () => {
          executeSchedule();
        },
      });
    };

    onMounted(() => {
      populateTimezoneOptions();
    });

    return {
      t,
      isVisible,
      sendType,
      minDate,
      selectedDate,
      selectedTime,
      timeOptions,
      selectedTimezone,
      timezoneOptions,
      perfectTiming,
      perfectTimingOptions,
      slowdown,
      slowdownOptions,
      intervalBetweenSends,
      intervalOptions,
      handleClose,
      handleSchedule,
    };
  },
});
